import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
    IconPlus, IconSearch, IconEdit, IconTrash, IconBriefcase, IconStar,
    IconUsers, IconCalendar, IconX,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useDebouncedValue } from '@/lib/hooks';
import SelectField from '../../../components/common/SelectField';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import jobService from '../../../services/jobService';
import PaginationBar from '@/components/common/PaginationBar';

const CATEGORY_TONE = {
    government: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
    defense: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
    bank: 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400',
    education: 'bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-400',
    private: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300',
};

const STATUS_TONE = {
    published: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
    draft: 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400',
    archived: 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400',
};

function Pagination({ page, totalPages, onChange, fmtNum }) {
    if (totalPages <= 1) return null;
    return (
        <div className="flex justify-center items-center gap-2 pt-2">
            <button type="button" disabled={page <= 1} onClick={() => onChange(page - 1)}
                className="px-3 h-[34px] text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 disabled:opacity-40 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => Math.abs(p - page) <= 2)
                .map(p => (
                    <button key={p} type="button" onClick={() => onChange(p)}
                        className={`w-[34px] h-[34px] text-sm rounded-lg border transition-colors ${p === page ? 'bg-primary text-white border-primary font-bold' : 'border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 font-semibold hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}>
                        {fmtNum(p)}
                    </button>
                ))
            }
            <button type="button" disabled={page >= totalPages} onClick={() => onChange(page + 1)}
                className="px-3 h-[34px] text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 disabled:opacity-40 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">›</button>
        </div>
    );
}

export default function AdminJobsPage() {
    const { t, currentLanguage } = useTranslation();
    const navigate = useNavigate();
    const isBn = currentLanguage === 'bn';
    const fmtNum = (n) => isBn ? String(n ?? 0).replace(/[0-9]/g, d => '০১২৩৪৫৬৭৮৯'[d]) : (n ?? 0).toLocaleString();

    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, 300);
    const [status, setStatus] = useState('');
    const [category, setCategory] = useState('');
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchJobs = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                ...(debouncedSearch && { search: debouncedSearch }),
                ...(status && { status }),
                ...(category && { category }),
                page,
                limit: 20,
            };
            const res = await jobService.adminList(params);
            setJobs(res.data || []);
            if (res.pagination) setPagination(res.pagination);
        } catch (error) {
            notifications.show({ title: t('common.error'), message: error.message, color: 'red' });
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, status, category, page, t]);

    useEffect(() => { fetchJobs(); }, [fetchJobs]);
    useEffect(() => { setPage(1); }, [debouncedSearch, status, category]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await jobService.remove(deleteTarget.id);
            notifications.show({ message: t('adminJobs.deleted'), color: 'green' });
            setDeleteTarget(null);
            fetchJobs();
        } catch (error) {
            notifications.show({ title: t('common.error'), message: error.message, color: 'red' });
        } finally {
            setDeleting(false);
        }
    };

    const statusOptions = [
        { value: '', label: t('adminJobs.allStatuses') },
        { value: 'published', label: t('adminJobs.status.published') },
        { value: 'draft', label: t('adminJobs.status.draft') },
        { value: 'archived', label: t('adminJobs.status.archived') },
    ];
    const categoryOptions = [
        { value: '', label: t('adminJobs.allCategories') },
        { value: 'government', label: t('jobs.categories.government') },
        { value: 'defense', label: t('jobs.categories.defense') },
        { value: 'bank', label: t('jobs.categories.bank') },
        { value: 'education', label: t('jobs.categories.education') },
        { value: 'private', label: t('jobs.categories.private') },
    ];

    const fmtDate = (d) => { try { return d ? format(new Date(d), 'dd MMM yyyy') : '—'; } catch { return '—'; } };
    const initials = (name) => (name || '').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

    const selectTriggerCls = 'h-11 px-3.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[13.5px]';
    const hasFilters = !!(search || status || category);

    return (
        <div className="w-full font-hind space-y-5 py-2 sm:py-4">

            {/* ── Header ── */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">{t('adminJobs.title')}</h1>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">{t('adminJobs.subtitle')}</p>
                </div>
                <button
                    type="button"
                    onClick={() => navigate('/admin/jobs/new')}
                    className="flex items-center gap-1.5 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-[13px] font-semibold rounded-lg transition-colors flex-shrink-0"
                >
                    <IconPlus size={16} stroke={2} /> {t('adminJobs.create')}
                </button>
            </div>

            {/* ── Filters ── */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-4 shadow-sm">
                <div className="flex gap-3 flex-col sm:flex-row sm:items-center">
                    {/* Search */}
                    <div className="flex-1 flex items-center gap-2.5 h-11 px-3.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
                        <IconSearch size={18} className="text-zinc-400 flex-shrink-0" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('adminJobs.searchPlaceholder')}
                            className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400"
                        />
                        {search && (
                            <button type="button" onClick={() => setSearch('')} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200">
                                <IconX size={16} />
                            </button>
                        )}
                    </div>
                    <div className="w-full sm:w-48">
                        <SelectField value={status} onChange={setStatus} options={statusOptions} triggerClassName={selectTriggerCls} />
                    </div>
                    <div className="w-full sm:w-48">
                        <SelectField value={category} onChange={setCategory} options={categoryOptions} triggerClassName={selectTriggerCls} />
                    </div>
                </div>
            </div>

            {/* ── Loading ── */}
            {loading && (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                </div>
            )}

            {/* ── Empty state ── */}
            {!loading && jobs.length === 0 && (
                <div className="flex flex-col items-center gap-3 py-20">
                    <div className="w-16 h-16 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                        <IconBriefcase size={28} stroke={1.5} className="text-zinc-400" />
                    </div>
                    <p className="text-base font-semibold text-zinc-600 dark:text-zinc-300">{t('adminJobs.empty')}</p>
                    {!hasFilters && (
                        <button type="button" onClick={() => navigate('/admin/jobs/new')}
                            className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors">
                            <IconPlus size={16} /> {t('adminJobs.create')}
                        </button>
                    )}
                </div>
            )}

            {/* ── Card grid ── */}
            {!loading && jobs.length > 0 && (
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {jobs.map((job) => (
                        <div
                            key={job.id}
                            className="group relative bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-4 transition-all hover:shadow-md hover:border-primary/40"
                        >
                            {/* Top row */}
                            <div className="flex items-start gap-3">
                                {job.logoUrl ? (
                                    <div className="w-11 h-11 rounded-xl overflow-hidden bg-white border border-zinc-200 dark:border-zinc-700 flex items-center justify-center flex-shrink-0">
                                        <img src={job.logoUrl} alt="" className="w-full h-full object-contain p-0.5" />
                                    </div>
                                ) : (
                                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary to-amber-400 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                                        {job.avatarText || initials(job.organization)}
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                        <span className="font-semibold text-[14.5px] text-zinc-900 dark:text-zinc-100 truncate">{job.organization}</span>
                                        {job.isFeatured && <IconStar size={14} className="text-amber-500 flex-shrink-0" />}
                                    </div>
                                    <p className="text-[12.5px] text-zinc-500 dark:text-zinc-400 truncate">{job.position}</p>
                                </div>
                                {/* Actions */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <button type="button" onClick={() => navigate(`/admin/jobs/${job.id}/edit`)} title={t('adminJobs.edit')}
                                        className="w-7 h-7 rounded-lg border border-zinc-200 dark:border-zinc-700 flex items-center justify-center text-zinc-500 hover:text-primary">
                                        <IconEdit size={15} />
                                    </button>
                                    <button type="button" onClick={() => setDeleteTarget(job)} title={t('adminJobs.delete')}
                                        className="w-7 h-7 rounded-lg border border-zinc-200 dark:border-zinc-700 flex items-center justify-center text-red-500 hover:text-red-600">
                                        <IconTrash size={15} />
                                    </button>
                                </div>
                            </div>

                            {/* Badges row */}
                            <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                                <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${CATEGORY_TONE[job.category] || CATEGORY_TONE.private}`}>
                                    {t(`jobs.categories.${job.category}`)}
                                </span>
                                <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[job.status] || STATUS_TONE.draft}`}>
                                    {t(`adminJobs.status.${job.status}`)}
                                </span>
                            </div>

                            {/* Meta row */}
                            <div className="flex items-center gap-3 mt-3 flex-wrap text-[11.5px] text-zinc-500 dark:text-zinc-400">
                                <span className="inline-flex items-center gap-1">
                                    <IconUsers size={14} className="flex-shrink-0" />
                                    {job.totalVacancies ? fmtNum(job.totalVacancies) : '—'}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    <IconCalendar size={14} className="flex-shrink-0" />
                                    {fmtDate(job.deadlineDate)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Pagination ── */}
            {!loading && jobs.length > 0 && (
                <PaginationBar page={pagination.page || page} totalPages={pagination.totalPages || 0} total={pagination.total} pageSize={20} currentCount={jobs.length} onPage={setPage} />
            )}

            <ConfirmDialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                loading={deleting}
                title={t('adminJobs.deleteConfirmTitle')}
                description={deleteTarget ? `${deleteTarget.organization} — ${deleteTarget.position}` : ''}
                confirmLabel={t('adminJobs.delete')}
            />
        </div>
    );
}
